import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { format, parseISO } from 'date-fns';

interface CancelSubscriptionModalProps {
  onClose: () => void;
}

// Cancellation reasons
const CANCELLATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using', label: 'Not using it enough' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'found_alternative', label: 'Found a better alternative' },
  { id: 'technical_issues', label: 'Technical issues' },
  { id: 'other', label: 'Other' }
];

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({ onClose }) => {
  const { cancelSubscription, getCurrentSubscription, isLoading } = useSubscription();
  const [subscription, setSubscription] = useState<any>(null);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [cancellationDate, setCancellationDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Fetch current subscription details
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const currentSubscription = await getCurrentSubscription();
        setSubscription(currentSubscription);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Unable to load your subscription details. Please try again later.');
      }
    };

    fetchSubscription();
  }, [getCurrentSubscription]);

  const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedReason(e.target.value);
  };

  const handleContinue = () => {
    if (!selectedReason) {
      setError('Please select a reason for cancellation');
      return;
    }
    
    setIsConfirming(true);
  };

  const handleCancel = async () => {
    if (!subscription?.id) {
      setError('Subscription information is missing');
      return;
    }

    try {
      setError(null);
      
      // Combine the selected reason with additional feedback
      const reasonText = selectedReason === 'other' 
        ? `Other: ${otherReason}` 
        : CANCELLATION_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
      
      const fullFeedback = feedback 
        ? `${reasonText}. Additional feedback: ${feedback}` 
        : reasonText;
      
      const result = await cancelSubscription(subscription.id, fullFeedback);
      
      if (result.success) {
        // Only use the end_date from the API response (which comes from cancel_at)
        setCancellationDate(result.end_date || null);
        setSuccess(true);
      } else {
        throw new Error('Cancellation was not successful');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel your subscription. Please try again later.');
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) {
      return 'the end of your billing period';
    }
    
    try {
      // First validate that the string is a valid ISO date
      const timestamp = Date.parse(dateString);
      if (isNaN(timestamp)) {
        console.error('Invalid date string:', dateString);
        return 'the end of your billing period';
      }
      
      // parseISO properly handles ISO 8601 strings with UTC "Z" suffix
      // JavaScript automatically converts to local timezone when displaying
      const date = parseISO(dateString);
      
      // Format with date and time
      return format(date, 'MMMM d, yyyy h:mm a');
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'the end of your billing period';
    }
  };

  // Show loading state
  if (isLoading && !subscription) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {success ? 'Subscription Cancelled' : isConfirming ? 'Confirm Cancellation' : 'Cancel Subscription'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="text-center">
            <div className="mb-4 p-4 bg-blue-900/50 border border-blue-500 rounded-lg">
              <p className="text-white mb-2">Your subscription has been cancelled.</p>
              <p className="text-gray-300">
                You'll continue to have access until{' '}
                <span className="font-semibold text-white">{formatDate(cancellationDate)}</span>.
              </p>
            </div>
            <p className="text-gray-400 mb-6">
              Thank you for your feedback. We're sorry to see you go and hope you'll consider rejoining in the future.
            </p>
            <button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        ) : isConfirming ? (
          <div>
            <p className="text-gray-300 mb-4">
              {subscription?.status === 'trialing' 
                ? 'Your trial will end after your two-day period. You won\'t be charged again.'
                : 'Your subscription will remain active until the end of your current billing period. You won\'t be charged again after that.'
              }
            </p>
            <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
              <p className="text-white">
                Are you sure you want to cancel your subscription?
              </p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setIsConfirming(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className={`bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-300 mb-6">
              We're sorry to see you go. Please let us know why you're cancelling so we can improve our service.
            </p>
            
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Reason for cancellation</label>
              <select
                value={selectedReason}
                onChange={handleReasonChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
              >
                <option value="">Select a reason</option>
                {CANCELLATION_REASONS.map(reason => (
                  <option key={reason.id} value={reason.id}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedReason === 'other' && (
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Please specify</label>
                <input
                  type="text"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                  placeholder="Tell us more..."
                />
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">Additional feedback (optional)</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white h-24 resize-none"
                placeholder="Is there anything else you'd like to share with us?"
              ></textarea>
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={onClose}
                className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancelSubscriptionModal; 